import { useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";

import { getBuildingData, getBuildingTypeData } from "libs/stats";
import { enterBuilding } from "redux/actions";

interface BuildingProps {
  slug: string;
}

interface BuildingWrapperProps {
  position: number;
}

const BuildingWrapper = styled.div<BuildingWrapperProps>`
  position: absolute;
  height: 100%;
  z-index: 2;
  pointer-events: auto;
  left: ${(props) => props.position * 0.2}%;
`;

const floatAnimation = keyframes`
  0% { transform: translate(-50%, -55%); }
  50% { transform: translate(-50%, -45%); }
  100% { transform: translate(-50%, -55%); }
`;

const buildingNameWrapperRightMixin = css`
  left: 54%;
`;

const BuildingNameWrapper = styled.div<{
  angle: "right" | "up" | "left" | "down";
}>`
  position: absolute;
  top: 30%;
  left: 50%;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  pointer-events: none;
  opacity: 0.2;
  transition: opacity 200ms ease-out;

  ${({ angle }) => {
    if (angle === "right") return buildingNameWrapperRightMixin;
  }}
`;

const buildingNameRightMixin = css`
  text-shadow: -1px 0 1px #aaaaaa, -2px 0 1px #aaaaaa, -3px 0 1px #aaaaaa,
    -4px 0 1px #aaaaaa, -5px 0 1px #aaaaaa, -6px 0 1px #aaaaaa,
    -7px 0 1px #aaaaaa, -8px 0 1px #aaaaaa, -9px 0 1px #aaaaaa,
    -10px 0 1px #aaaaaa, -18px 0 6px rgba(16, 16, 16, 0.4),
    -22px 0 10px rgba(16, 16, 16, 0.2), -25px 0 35px rgba(16, 16, 16, 0.2),
    -30px 0 60px rgba(16, 16, 16, 0.4);
`;

const buildingNameUpMixin = css`
  text-shadow: 0 1px 1px #aaaaaa, 0 2px 1px #aaaaaa, 0 3px 1px #aaaaaa,
    0 4px 1px #aaaaaa, 0 5px 1px #aaaaaa, 0 6px 1px #aaaaaa, 0 7px 1px #aaaaaa,
    0 8px 1px #aaaaaa, 0 9px 1px #aaaaaa, 0 10px 1px #aaaaaa,
    0 18px 6px rgba(16, 16, 16, 0.4), 0 22px 10px rgba(16, 16, 16, 0.2),
    0 25px 35px rgba(16, 16, 16, 0.2), 0 30px 60px rgba(16, 16, 16, 0.4);
`;

const BuildingName = styled.div<{ angle: "right" | "up" | "left" | "down" }>`
  font-size: 12vh;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;

  ${({ angle }) => {
    if (angle === "right") return buildingNameRightMixin;
    if (angle === "up") return buildingNameUpMixin;
  }}
`;

const BuildingImage = styled.img`
  height: 100%;
  cursor: pointer;

  &:hover {
    + ${BuildingNameWrapper} {
      opacity: 1;
    }
  }
`;

export default function Building({ slug }: BuildingProps) {
  const dispatch = useDispatch();
  const buildingData = getBuildingData(slug);
  const buildingTypeData = getBuildingTypeData(buildingData.type);

  return (
    <BuildingWrapper position={buildingData.position}>
      <BuildingImage
        src={buildingData.exteriorImage}
        onClick={() =>
          dispatch(
            enterBuilding({ building: slug, screen: null, dialog: null })
          )
        }
      />
      <BuildingNameWrapper angle={buildingData.angle}>
        <BuildingName angle={buildingData.angle}>
          {buildingTypeData.name}
        </BuildingName>
      </BuildingNameWrapper>
    </BuildingWrapper>
  );
}
